import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, CardText,
  Input, Label, Button, Table, Form,
} from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { ApiProxyGuard } from '../components/ApiProxyGuard';
import { ApiHealthStrip } from '../components/ApiHealthStrip';
import { wpFetch, isWpProxyEnabled } from '../wp/wpClient';

/**
 * WP API — zentrale Steuer-API fürs WordPress-Core-Plugin: Plugin-Store &
 * Updates, dynamischer Rechtstext-Generator, Instanz-Telemetrie. Verdrahtet gegen
 * /proxy/wp (Admin-Fläche /v1/admin/*).
 */
// #116/#114 — Delegat auf die gemeinsame Guard-Komponente (wie EbillApi).
function ProxyGuard({ children }) {
  return (
    <ApiProxyGuard enabled={isWpProxyEnabled()} label="wp" envVar="WP_API_URL" proxyPath="/proxy/wp">
      {children}
    </ApiProxyGuard>
  );
}

const TIERS = ['basic', 'advanced', 'agency'];
const tierBadge = (t) => <Badge color={t === 'agency' ? 'primary' : t === 'advanced' ? 'info' : 'secondary'} pill>{t}</Badge>;

const CARDS = [
  { to: '/admin/apis/wp/plugins', icon: '🧩', title: 'Plugin-Store', status: 'live',
    description: 'Katalog der auslieferbaren Erweiterungen: Slug, Version, Mindest-Tarif, Storage-Key.' },
  { to: '/admin/apis/wp/templates', icon: '⚖️', title: 'Rechtstexte', status: 'live',
    description: 'Vorlagen für Impressum/Datenschutz mit Platzhaltern und bedingten Bausteinen — inkl. Vorschau.' },
  { to: '/admin/apis/wp/instances', icon: '🌐', title: 'Instanzen', status: 'live',
    description: 'Registrierte WordPress-Instanzen: Domain, Tarifstufe, Status. Key-Ausgabe einmalig.' },
  { to: '/admin/apis/wp/telemetry', icon: '📈', title: 'Telemetrie', status: 'live',
    description: 'Herzschlag der Instanzen: WP-/PHP-Versionen, aktive Plugins, gemeldete Fehler.' },
  { to: '/admin/apis/wp/modules', icon: '🎚️', title: 'Modul-Tarife', status: 'live',
    description: 'Feature-Module den Tarifstufen zuordnen (Katalog-Default überschreibbar) — steuert die Freischaltung je Instanz.' },
  { to: '/admin/apis/wp/security', icon: '🛡️', title: 'Angriffsabwehr', status: 'live',
    description: 'Abwehr-Zähler je Instanz: verarbeitete Anfragen, Blocks und Captcha-Challenges (VWS-Security-Modul).' },
  { to: '/admin/apis/wp/compliance', icon: '⚖️', title: 'Rechts-Compliance', status: 'live',
    description: 'Impressum/Datenschutz je Instanz: verlinkt + erreichbar? Non-Compliant filtern und Re-Check anstoßen.' },
  { to: '/admin/apis/wp/vuln', icon: '🔓', title: 'Schwachstellen', status: 'live',
    description: 'Sites mit offenen Schwachstellen, schwerste zuerst. Quelle: täglicher Inventar-Check der VWS-Instanzen.' },
];

// ── Übersicht ───────────────────────────────────────────────────────────
export function WpOverviewPage() {
  const enabled = isWpProxyEnabled();
  const health = useQuery({ queryKey: ['wp-health'], queryFn: () => wpFetch('/healthz'), refetchInterval: 30_000, retry: false, enabled });
  const mon = useQuery({ queryKey: ['wp-monitoring'], queryFn: () => wpFetch('/v1/monitoring'), refetchInterval: 20_000, retry: false, enabled });
  const m = mon.data || {};

  return (
    <div>
      <PageHeader title="WP API · Übersicht"
        subtitle="Plugin-Store & Updates, Rechtstext-Generator und Instanz-Telemetrie für das WordPress-Core-Plugin." />
      <ProxyGuard>
        <ApiHealthStrip health={health}>
          {m.status && <Badge color={m.status === 'ok' ? 'success' : 'warning'} pill className="ms-2">{m.status}</Badge>}
        </ApiHealthStrip>
        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Instanzen</CardTitle>
            <CardText className="fs-4">{m.instances_active ?? '–'}<span className="fs-6 text-muted">/{m.instances_total ?? '–'}</span></CardText>
            <div className="small text-muted">aktiv / gesamt</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Plugins</CardTitle>
            <CardText className="fs-4">{m.plugins_enabled ?? '–'}<span className="fs-6 text-muted">/{m.plugins_total ?? '–'}</span></CardText>
            <div className="small text-muted">freigegeben / gesamt</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Vorlagen</CardTitle>
            <CardText className="fs-4">{m.templates_total ?? '–'}</CardText>
            <div className="small text-muted">Rechtstext-Templates</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Pings (24h)</CardTitle>
            <CardText className="fs-4">{m.telemetry_pings_24h ?? '–'}</CardText>
            <div className="small text-muted">Telemetrie-Herzschlag</div>
          </CardBody></Card></Col>
        </Row>
        <OverviewGrid cards={CARDS} />
      </ProxyGuard>
    </div>
  );
}

// ── Plugin-Store ────────────────────────────────────────────────────────
const NEW_PLUGIN = { slug: '', name: '', version: '1.0.0', min_tier: 'basic', storage_key: '', description: '', enabled: true };
export function WpPluginsPage() {
  const qc = useQueryClient();
  const enabled = isWpProxyEnabled();
  const list = useQuery({ queryKey: ['wp-plugins'], queryFn: () => wpFetch('/v1/admin/plugins'), refetchInterval: 30_000, retry: false, enabled });
  const [form, setForm] = useState(NEW_PLUGIN);
  const items = list.data?.plugins || [];

  const save = useMutation({
    mutationFn: () => wpFetch('/v1/admin/plugins', { method: 'POST', body: {
      slug: form.slug, name: form.name, version: form.version, min_tier: form.min_tier,
      storage_key: form.storage_key || undefined, description: form.description || undefined,
    } }),
    onSuccess: () => { setForm(NEW_PLUGIN); qc.invalidateQueries({ queryKey: ['wp-plugins'] }); },
  });
  const toggle = useMutation({
    mutationFn: (p) => wpFetch(`/v1/admin/plugins/${p.id}`, { method: 'PATCH', body: { enabled: !p.enabled } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wp-plugins'] }),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader title="WP API · Plugin-Store" subtitle="Auslieferbare Erweiterungen: Version, Mindest-Tarif und Storage-Key (bunny/Cloudflare) pflegen." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Neues Plugin</CardTitle>
          <Form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
            <Row className="g-2">
              <Col md={3}><Label className="small">Slug</Label><Input bsSize="sm" value={form.slug} onChange={set('slug')} placeholder="valitype-seo" /></Col>
              <Col md={3}><Label className="small">Name</Label><Input bsSize="sm" value={form.name} onChange={set('name')} placeholder="Valitype SEO" /></Col>
              <Col md={2}><Label className="small">Version</Label><Input bsSize="sm" value={form.version} onChange={set('version')} /></Col>
              <Col md={2}><Label className="small">Mindest-Tarif</Label>
                <Input type="select" bsSize="sm" value={form.min_tier} onChange={set('min_tier')}>
                  {TIERS.map((t) => <option key={t}>{t}</option>)}
                </Input>
              </Col>
              <Col md={2}><Label className="small">Storage-Key</Label><Input bsSize="sm" value={form.storage_key} onChange={set('storage_key')} placeholder="plugins/valitype-seo-1.0.0.zip" /></Col>
              <Col md={12}><Label className="small">Beschreibung</Label><Input bsSize="sm" value={form.description} onChange={set('description')} /></Col>
            </Row>
            <Button size="sm" color="primary" className="mt-2" disabled={save.isPending} type="submit">{save.isPending ? <Spinner size="sm" /> : 'Plugin speichern'}</Button>
            {save.isError && <Alert color="danger" className="small mt-2">{save.error?.message}</Alert>}
          </Form>
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Katalog <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {list.isLoading ? <Spinner size="sm" /> : !items.length ? <CardText className="small text-muted">Noch keine Plugins.</CardText> : (
            <Table size="sm" responsive hover className="small">
              <thead><tr><th>Slug</th><th>Version</th><th>Mindest-Tarif</th><th>Storage-Key</th><th>Status</th><th></th></tr></thead>
              <tbody>{items.map((p) => (
                <tr key={p.id}>
                  <td>{p.slug}<div className="text-muted">{p.name}</div></td>
                  <td>{p.version}</td>
                  <td>{tierBadge(p.min_tier)}</td>
                  <td className="text-muted"><code>{p.storage_key || '—'}</code></td>
                  <td>{p.enabled ? <Badge color="success" pill>aktiv</Badge> : <Badge color="secondary" pill>deaktiviert</Badge>}</td>
                  <td><Button size="sm" color="link" className="p-0" disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>{p.enabled ? 'deaktivieren' : 'aktivieren'}</Button></td>
                </tr>
              ))}</tbody>
            </Table>
          )}
          {toggle.isError && <Alert color="danger" className="small mt-2">{toggle.error?.message}</Alert>}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Rechtstext-Vorlagen ─────────────────────────────────────────────────
const SAMPLE_COMPANY = { name: 'Muster GmbH', street: 'Musterweg 1', zip: '12345', city: 'Musterstadt', country: 'Deutschland', email: 'info@muster.de', vat_id: 'DE123456789' };
export function WpTemplatesPage() {
  const enabled = isWpProxyEnabled();
  const list = useQuery({ queryKey: ['wp-templates'], queryFn: () => wpFetch('/v1/admin/templates'), refetchInterval: 60_000, retry: false, enabled });
  const items = list.data?.templates || [];
  const [selected, setSelected] = useState(null);
  const [features, setFeatures] = useState({ matomo: true, woocommerce: false, contact_form: true, newsletter: false });

  const preview = useMutation({
    mutationFn: (body) => wpFetch('/v1/admin/templates/preview', { method: 'POST', body }),
  });
  const runPreview = (tpl) => {
    setSelected(tpl);
    preview.mutate({ body: tpl.body, company: SAMPLE_COMPANY, features });
  };

  return (
    <div>
      <PageHeader title="WP API · Rechtstexte" subtitle="Vorlagen für Impressum/Datenschutz mit Platzhaltern ({{ company.name }}) und bedingten Blöcken ({{#if features.matomo}} … {{/if}})." />
      <ProxyGuard>
        <Row className="g-3">
          <Col md={6}><Card><CardBody>
            <CardTitle tag="h6">Vorlagen <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
            {list.isLoading ? <Spinner size="sm" /> : !items.length ? <CardText className="small text-muted">Keine Vorlagen (Seed 002 liefert Impressum + Datenschutz).</CardText> : (
              <Table size="sm" responsive hover className="small">
                <thead><tr><th>Typ</th><th>Sprache</th><th>v</th><th>Status</th><th></th></tr></thead>
                <tbody>{items.map((t) => (
                  <tr key={t.id}>
                    <td>{t.type}<div className="text-muted">{t.title}</div></td>
                    <td>{t.locale}</td>
                    <td>{t.version}</td>
                    <td>{t.enabled ? <Badge color="success" pill>aktiv</Badge> : <Badge color="secondary" pill>aus</Badge>}</td>
                    <td><Button size="sm" color="link" className="p-0" onClick={() => runPreview(t)}>Vorschau</Button></td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
            <div className="small text-muted mt-2">Beispiel-Features für die Vorschau:</div>
            <div className="d-flex flex-wrap gap-3 mt-1">
              {Object.keys(features).map((k) => (
                <Label key={k} className="small mb-0"><Input type="checkbox" className="me-1" checked={features[k]} onChange={(e) => setFeatures((f) => ({ ...f, [k]: e.target.checked }))} />{k}</Label>
              ))}
            </div>
          </CardBody></Card></Col>
          <Col md={6}><Card><CardBody>
            <CardTitle tag="h6">Vorschau {selected ? <Badge color="light" className="text-dark border">{selected.type}/{selected.locale}</Badge> : null}</CardTitle>
            {preview.isPending ? <Spinner size="sm" /> : preview.isError ? <Alert color="danger" className="small">{preview.error?.message}</Alert>
              : preview.data ? (
                // #501: Upstream-HTML nicht in die Orbit-Origin injizieren
                // (XSS → Token-Diebstahl). Stattdessen sandboxed iframe (kein
                // Script, kein same-origin, kein Top-Nav) via srcdoc.
                <iframe
                  title="Vorschau"
                  sandbox=""
                  srcDoc={preview.data.html}
                  className="border rounded"
                  style={{ width: '100%', height: 480, background: '#fff' }}
                />
              )
              : <CardText className="small text-muted">Wähle links eine Vorlage → „Vorschau".</CardText>}
          </CardBody></Card></Col>
        </Row>
      </ProxyGuard>
    </div>
  );
}

// ── Instanzen ───────────────────────────────────────────────────────────
export function WpInstancesPage() {
  const qc = useQueryClient();
  const enabled = isWpProxyEnabled();
  const list = useQuery({ queryKey: ['wp-instances'], queryFn: () => wpFetch('/v1/admin/instances'), refetchInterval: 30_000, retry: false, enabled });
  const [form, setForm] = useState({ domain: '', tier: 'basic' });
  const [issuedKey, setIssuedKey] = useState(null);
  const items = list.data?.instances || [];

  const create = useMutation({
    mutationFn: () => wpFetch('/v1/admin/instances', { method: 'POST', body: { domain: form.domain, tier: form.tier } }),
    onSuccess: (row) => { setIssuedKey(row?.key || null); setForm({ domain: '', tier: 'basic' }); qc.invalidateQueries({ queryKey: ['wp-instances'] }); },
  });
  const patch = useMutation({
    mutationFn: (i) => wpFetch(`/v1/admin/instances/${i.id}`, { method: 'PATCH', body: { status: i.status === 'active' ? 'suspended' : 'active' } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wp-instances'] }),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader title="WP API · Instanzen" subtitle="Registrierte WordPress-Instanzen (Domain-gebunden). Bei CORE-Anbindung ist CORE die Quelle der Wahrheit; hier lokal angelegte Keys dienen dem Fallback/Test." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Instanz anlegen</CardTitle>
          <Form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <Row className="g-2 align-items-end">
              <Col md={5}><Label className="small">Domain</Label><Input bsSize="sm" value={form.domain} onChange={set('domain')} placeholder="kunde.example.com" /></Col>
              <Col md={3}><Label className="small">Tarifstufe</Label>
                <Input type="select" bsSize="sm" value={form.tier} onChange={set('tier')}>{TIERS.map((t) => <option key={t}>{t}</option>)}</Input>
              </Col>
              <Col md={2}><Button size="sm" color="primary" disabled={create.isPending} type="submit">{create.isPending ? <Spinner size="sm" /> : 'Anlegen'}</Button></Col>
            </Row>
            {create.isError && <Alert color="danger" className="small mt-2">{create.error?.message}</Alert>}
          </Form>
          {issuedKey && (
            <Alert color="success" className="small mt-2">
              <strong>Key (nur einmalig sichtbar):</strong> <code style={{ wordBreak: 'break-all' }}>{issuedKey}</code>
              <Button size="sm" color="link" className="p-0 ms-2" onClick={() => setIssuedKey(null)}>ausblenden</Button>
            </Alert>
          )}
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Instanzen <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {list.isLoading ? <Spinner size="sm" /> : !items.length ? <CardText className="small text-muted">Noch keine Instanzen.</CardText> : (
            <Table size="sm" responsive hover className="small">
              <thead><tr><th>Domain</th><th>Tarif</th><th>Status</th><th>Letzter Ping</th><th>Zuletzt gesehen</th><th></th></tr></thead>
              <tbody>{items.map((i) => (
                <tr key={i.id}>
                  <td>{i.domain}<div className="text-muted"><code>{i.key_prefix || '—'}</code></div></td>
                  <td>{tierBadge(i.tier)}</td>
                  <td>{i.status === 'active' ? <Badge color="success" pill>aktiv</Badge> : <Badge color="danger" pill>gesperrt</Badge>}</td>
                  <td className="text-muted">{i.last_ping_at ? new Date(i.last_ping_at).toLocaleString() : '—'}</td>
                  <td className="text-muted">{i.last_seen_at ? new Date(i.last_seen_at).toLocaleString() : '—'}</td>
                  <td><Button size="sm" color="link" className="p-0" disabled={patch.isPending} onClick={() => patch.mutate(i)}>{i.status === 'active' ? 'sperren' : 'freigeben'}</Button></td>
                </tr>
              ))}</tbody>
            </Table>
          )}
          {patch.isError && <Alert color="danger" className="small mt-2">{patch.error?.message}</Alert>}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Telemetrie ──────────────────────────────────────────────────────────
export function WpTelemetryPage() {
  const enabled = isWpProxyEnabled();
  const q = useQuery({ queryKey: ['wp-telemetry'], queryFn: () => wpFetch('/v1/admin/telemetry'), refetchInterval: 30_000, retry: false, enabled });
  const s = q.data?.summary || {};
  const latest = q.data?.latest || [];

  return (
    <div>
      <PageHeader title="WP API · Telemetrie" subtitle="Herzschlag der Instanzen: WP-/PHP-Versionen, aktive Plugins, gemeldete Fehler." />
      <ProxyGuard>
        <Row className="g-3 mb-3">
          <Col md={4}><Card><CardBody><CardTitle tag="h6">Pings (24h)</CardTitle><CardText className="fs-4">{s.pings_24h ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={4}><Card><CardBody><CardTitle tag="h6">Aktive Instanzen (24h)</CardTitle><CardText className="fs-4">{s.active_instances_24h ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={4}><Card><CardBody><CardTitle tag="h6">WP-Versionen</CardTitle>
            <div className="d-flex flex-wrap gap-1 mt-1">{(s.wp_versions || []).map((v) => <Badge key={v.wp_version} color="light" className="text-dark border">{v.wp_version} · {v.n}</Badge>)}</div>
          </CardBody></Card></Col>
        </Row>
        <Card><CardBody>
          <CardTitle tag="h6">Letzter Ping je Instanz <Badge color="secondary" pill>{latest.length}</Badge></CardTitle>
          {q.isLoading ? <Spinner size="sm" /> : !latest.length ? <CardText className="small text-muted">Noch keine Telemetrie empfangen.</CardText> : (
            <Table size="sm" responsive hover className="small">
              <thead><tr><th>Domain</th><th>WP</th><th>PHP</th><th>Plugin</th><th>Aktive Plugins</th><th>Fehler</th><th>Cache</th><th>Zeit</th></tr></thead>
              <tbody>{latest.map((t) => (
                <tr key={t.instance_id}>
                  <td>{t.domain}</td>
                  <td>{t.wp_version || '—'}</td>
                  <td>{t.php_version || '—'}</td>
                  <td>{t.plugin_version || '—'}</td>
                  <td>{t.active_plugin_count ?? '—'}</td>
                  <td>{t.error_count ? <Badge color="danger" pill>{t.error_count}</Badge> : '0'}</td>
                  <td>{t.cache_hit_ratio != null ? `${t.cache_hit_ratio}%` : '—'}</td>
                  <td className="text-muted">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Modul-Tarife (#144) ─────────────────────────────────────────────────
// Modul→Tarif-Zuordnung. Katalog-Default ist überschreibbar; „zurücksetzen"
// löscht den Override (PATCH min_tier=null). GET/PATCH /v1/admin/modules.
export function WpModulesPage() {
  const qc = useQueryClient();
  const enabled = isWpProxyEnabled();
  const list = useQuery({ queryKey: ['wp-modules'], queryFn: () => wpFetch('/v1/admin/modules'), refetchInterval: 60_000, retry: false, enabled });
  const items = list.data?.modules || [];

  const patch = useMutation({
    mutationFn: ({ id, min_tier }) => wpFetch(`/v1/admin/modules/${id}`, { method: 'PATCH', body: { min_tier } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wp-modules'] }),
  });

  return (
    <div>
      <PageHeader title="WP API · Modul-Tarife"
        subtitle="Feature-Module den Tarifstufen zuordnen. Der Katalog-Default lässt sich pro Modul überschreiben — das steuert, ab welcher Tarifstufe eine Instanz das Modul freigeschaltet bekommt." />
      <ProxyGuard>
        <Card><CardBody>
          <CardTitle tag="h6">Module <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {list.isLoading ? <Spinner size="sm" /> : !items.length ? <CardText className="small text-muted">Keine Module.</CardText> : (
            <Table size="sm" responsive hover className="small align-middle">
              <thead><tr><th>Modul</th><th>Katalog-Default</th><th>Aktiver Mindest-Tarif</th><th>Quelle</th><th></th></tr></thead>
              <tbody>{items.map((m) => (
                <tr key={m.id}>
                  <td><code>{m.id}</code></td>
                  <td>{tierBadge(m.default_min_tier)}</td>
                  <td style={{ maxWidth: 160 }}>
                    <Input type="select" bsSize="sm" value={m.min_tier} disabled={patch.isPending}
                      onChange={(e) => patch.mutate({ id: m.id, min_tier: e.target.value })}>
                      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </Input>
                  </td>
                  <td>{m.overridden ? <Badge color="info" pill>Override</Badge> : <Badge color="light" className="text-dark border" pill>Default</Badge>}</td>
                  <td>{m.overridden && (
                    <Button size="sm" color="link" className="p-0" disabled={patch.isPending}
                      onClick={() => patch.mutate({ id: m.id, min_tier: null })}>zurücksetzen</Button>
                  )}</td>
                </tr>
              ))}</tbody>
            </Table>
          )}
          {patch.isError && <Alert color="danger" className="small mt-2">{patch.error?.message}</Alert>}
          <div className="small text-muted mt-2">Änderungen greifen bei der nächsten Feature-Auflösung der jeweiligen Instanz.</div>
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Angriffsabwehr (#149) ───────────────────────────────────────────────
// Abwehr-Zähler je Instanz + Aggregat. GET /v1/admin/security/stats.
export function WpSecurityPage() {
  const enabled = isWpProxyEnabled();
  const q = useQuery({ queryKey: ['wp-security-stats'], queryFn: () => wpFetch('/v1/admin/security/stats'), refetchInterval: 30_000, retry: false, enabled });
  const total = q.data?.total || { processed: 0, blocked: 0, captcha: 0 };
  const rows = q.data?.instances || [];
  const available = q.data?.available;

  return (
    <div>
      <PageHeader title="WP API · Angriffsabwehr"
        subtitle="Abwehr-Zähler des VWS-Security-Moduls je Instanz: verarbeitete Anfragen, harte Blocks und Captcha-Challenges." />
      <ProxyGuard>
        {available === false && <Alert color="warning" className="small">Security-Metriken derzeit nicht abrufbar — Werte können unvollständig sein.</Alert>}
        <Row className="g-3 mb-3">
          <Col md={4}><Card><CardBody><CardTitle tag="h6">Verarbeitet</CardTitle><CardText className="fs-4">{total.processed?.toLocaleString?.() ?? total.processed}</CardText><div className="small text-muted">Anfragen gesamt</div></CardBody></Card></Col>
          <Col md={4}><Card><CardBody><CardTitle tag="h6">Geblockt</CardTitle><CardText className="fs-4 text-danger">{total.blocked?.toLocaleString?.() ?? total.blocked}</CardText><div className="small text-muted">harte Blocks</div></CardBody></Card></Col>
          <Col md={4}><Card><CardBody><CardTitle tag="h6">Captcha</CardTitle><CardText className="fs-4 text-warning">{total.captcha?.toLocaleString?.() ?? total.captcha}</CardText><div className="small text-muted">Challenges</div></CardBody></Card></Col>
        </Row>
        <Card><CardBody>
          <CardTitle tag="h6">Je Instanz <Badge color="secondary" pill>{rows.length}</Badge></CardTitle>
          {q.isLoading ? <Spinner size="sm" /> : !rows.length ? <CardText className="small text-muted">Keine Instanzen.</CardText> : (
            <Table size="sm" responsive hover className="small">
              <thead><tr><th>Domain</th><th>Tarif</th><th>Status</th><th className="text-end">Verarbeitet</th><th className="text-end">Geblockt</th><th className="text-end">Captcha</th><th>Aktualisiert</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.instance_id}>
                  <td>{r.domain}</td>
                  <td>{tierBadge(r.tier)}</td>
                  <td>{r.status === 'active' ? <Badge color="success" pill>aktiv</Badge> : <Badge color="danger" pill>{r.status}</Badge>}</td>
                  <td className="text-end">{r.processed?.toLocaleString?.() ?? r.processed}</td>
                  <td className="text-end">{r.blocked ? <Badge color="danger" pill>{r.blocked.toLocaleString?.() ?? r.blocked}</Badge> : '0'}</td>
                  <td className="text-end">{r.captcha ? <Badge color="warning" pill>{r.captcha.toLocaleString?.() ?? r.captcha}</Badge> : '0'}</td>
                  <td className="text-muted">{r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}</td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Rechts-Compliance (#150) ────────────────────────────────────────────
// Impressum/Datenschutz je Instanz: verlinkt + erreichbar? Non-Compliant
// filtern und Re-Check anstoßen. GET /v1/admin/legal/compliance, POST …/recheck.
const CheckMark = ({ ok }) => ok ? <Badge color="success" pill>✓</Badge> : <Badge color="danger" pill>✗</Badge>;
export function WpCompliancePage() {
  const qc = useQueryClient();
  const enabled = isWpProxyEnabled();
  const [onlyBad, setOnlyBad] = useState(false);
  const list = useQuery({
    queryKey: ['wp-compliance', onlyBad],
    queryFn: () => wpFetch(`/v1/admin/legal/compliance${onlyBad ? '?compliant=false' : ''}`),
    refetchInterval: 60_000, retry: false, enabled,
  });
  const rows = list.data?.instances || [];

  const recheck = useMutation({
    mutationFn: (instanceId) => wpFetch(`/v1/admin/legal/compliance/${instanceId}/recheck`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wp-compliance'] }),
  });

  return (
    <div>
      <PageHeader title="WP API · Rechts-Compliance"
        subtitle="Ist auf jeder Instanz Impressum und Datenschutz verlinkt und erreichbar? Der Re-Check stößt eine erneute Prüfung (via Probe) an." />
      <ProxyGuard>
        <div className="d-flex align-items-center gap-3 mb-2">
          <Label className="small mb-0"><Input type="checkbox" className="me-1" checked={onlyBad} onChange={(e) => setOnlyBad(e.target.checked)} />nur Non-Compliant</Label>
          {recheck.isError && <Alert color="danger" className="small mb-0 py-1 px-2">{recheck.error?.message}</Alert>}
        </div>
        <Card><CardBody>
          <CardTitle tag="h6">Instanzen <Badge color="secondary" pill>{rows.length}</Badge></CardTitle>
          {list.isLoading ? <Spinner size="sm" /> : !rows.length ? <CardText className="small text-muted">{onlyBad ? 'Alle Instanzen compliant. 🎉' : 'Keine Instanzen.'}</CardText> : (
            <Table size="sm" responsive hover className="small align-middle">
              <thead><tr>
                <th>Domain</th><th>Tarif</th>
                <th>Impressum<div className="text-muted fw-normal">verlinkt / erreichbar</div></th>
                <th>Datenschutz<div className="text-muted fw-normal">verlinkt / erreichbar</div></th>
                <th>Compliant</th><th>Geprüft</th><th></th>
              </tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.instance_id}>
                  <td>{r.domain}</td>
                  <td>{tierBadge(r.tier)}</td>
                  <td><CheckMark ok={r.impressum_linked} /> <CheckMark ok={r.impressum_reachable} /></td>
                  <td><CheckMark ok={r.datenschutz_linked} /> <CheckMark ok={r.datenschutz_reachable} /></td>
                  <td>{r.compliant ? <Badge color="success" pill>compliant</Badge> : r.checked_at ? <Badge color="danger" pill>fehlerhaft</Badge> : <Badge color="secondary" pill>ungeprüft</Badge>}</td>
                  <td className="text-muted">{r.checked_at ? new Date(r.checked_at).toLocaleString() : '—'}</td>
                  <td><Button size="sm" color="link" className="p-0" disabled={recheck.isPending} onClick={() => recheck.mutate(r.instance_id)}>Re-Check</Button></td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Schwachstellen je Site (#176 Sicht 3) ───────────────────────────────
// Support-Arbeitsliste: welche Kunden-Site hat offene Lücken, wie schwer, seit
// wann. Quelle ist der tägliche /v1/vuln/check der VWS-Instanzen — der Befund
// wird dabei je Instanz festgehalten (wp_vuln_findings). Kein eigener Scan von
// hier aus: der Auslöser sitzt auf der Instanz, nicht bei uns.
const SEVERITY_COLOR = { critical: 'danger', high: 'warning', medium: 'info', low: 'secondary', unknown: 'secondary', none: 'success' };
const sevBadge = (s) => <Badge color={SEVERITY_COLOR[s] || 'secondary'} pill>{s}</Badge>;

export function WpVulnPage() {
  const enabled = isWpProxyEnabled();
  const [onlyBad, setOnlyBad] = useState(true);   // Default = Arbeitsliste, nicht Vollbestand
  const [minSeverity, setMinSeverity] = useState('');
  const list = useQuery({
    queryKey: ['wp-vuln-sites', onlyBad, minSeverity],
    queryFn: () => {
      const p = new URLSearchParams();
      if (onlyBad) p.set('vulnerable', 'true');
      if (minSeverity) p.set('severity', minSeverity);
      return wpFetch(`/v1/admin/vuln/sites${p.toString() ? `?${p}` : ''}`);
    },
    refetchInterval: 60_000, retry: false, enabled,
  });
  const rows = list.data?.instances || [];
  const sum = (k) => rows.reduce((n, r) => n + Number(r[k] || 0), 0);

  return (
    <div>
      <PageHeader title="WP API · Schwachstellen"
        subtitle="Kunden-Sites mit bekannten Lücken in Core, Plugins oder Themes — schwerste zuerst. Der Befund stammt aus dem täglichen Inventar-Check der VWS-Instanzen." />
      <ProxyGuard>
        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Betroffene Sites</CardTitle>
            <CardText className="fs-4">{rows.filter((r) => Number(r.affected_count) > 0).length}</CardText></CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Kritisch</CardTitle>
            <CardText className="fs-4 text-danger">{sum('n_critical')}</CardText></CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Hoch</CardTitle>
            <CardText className="fs-4 text-warning">{sum('n_high')}</CardText></CardBody></Card></Col>
          {/* „Nie geprüft" ist kein Nullwert, sondern ein eigener Zustand: die
              Instanz meldet sich nicht (Modul aus, Cron tot, Lizenz weg). */}
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Nie geprüft</CardTitle>
            <CardText className="fs-4">{rows.filter((r) => !r.checked_at).length}</CardText></CardBody></Card></Col>
        </Row>

        <div className="d-flex align-items-center gap-3 mb-2">
          <Label className="small mb-0"><Input type="checkbox" className="me-1" checked={onlyBad} onChange={(e) => setOnlyBad(e.target.checked)} />nur betroffene Sites</Label>
          <Label className="small mb-0 d-flex align-items-center gap-1">ab Schwere
            <Input type="select" bsSize="sm" value={minSeverity} onChange={(e) => setMinSeverity(e.target.value)}>
              <option value="">alle</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
              <option value="critical">critical</option>
            </Input>
          </Label>
          {list.isError && <Alert color="danger" className="small mb-0 py-1 px-2">{list.error?.message}</Alert>}
        </div>

        <Card><CardBody>
          <CardTitle tag="h6">Sites <Badge color="secondary" pill>{rows.length}</Badge></CardTitle>
          {list.isLoading ? <Spinner size="sm" /> : !rows.length ? (
            <CardText className="small text-muted">{onlyBad ? 'Keine Site mit offenen Schwachstellen. 🎉' : 'Keine Instanzen.'}</CardText>
          ) : (
            <Table size="sm" responsive hover className="small align-middle">
              <thead><tr>
                <th>Domain</th><th>Tarif</th><th>Schwerste</th>
                <th>Pakete<div className="text-muted fw-normal">betroffen / geprüft</div></th>
                <th className="text-end">Krit.</th><th className="text-end">Hoch</th>
                <th className="text-end">Mittel</th><th className="text-end">Niedrig</th>
                <th>Geprüft</th>
              </tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.instance_id}>
                  <td>{r.domain}</td>
                  <td>{tierBadge(r.tier)}</td>
                  <td>{r.checked_at ? sevBadge(r.worst_severity || 'none') : <Badge color="secondary" pill>ungeprüft</Badge>}</td>
                  <td>{r.checked_at ? `${r.affected_count} / ${r.items_checked}` : '—'}</td>
                  <td className="text-end">{r.n_critical ?? '—'}</td>
                  <td className="text-end">{r.n_high ?? '—'}</td>
                  <td className="text-end">{r.n_medium ?? '—'}</td>
                  <td className="text-end">{r.n_low ?? '—'}</td>
                  <td className="text-muted">{r.checked_at ? new Date(r.checked_at).toLocaleString() : 'nie'}</td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}
